import { memo, useEffect, useRef, useCallback, useState } from 'react';
import cx from 'classnames';
import ReactDOM from 'react-dom';
import moment from 'moment-jalaali';
import { useDispatch, useSelector } from 'react-redux';
import { Timeline } from 'vis-timeline/standalone';
import { useMediaQuery } from 'react-responsive';
import { isObjEmpty } from 'Utils';
import { setSelectedMarker, setSidebar } from 'Actions';
import {
	TimelineGroupItem,
	TimelineCluster,
	TimelineSingleItem
} from 'Elements';
import 'moment/locale/fa';
moment.loadPersian({ dialect: 'persian-modern' });
import styles from 'partials/timeline.module.scss';

export const VisTimelineVanillaJs = memo(() => {
	const instance = useRef();
	const timelineRef = useRef(null);
	// const timelineItemRef = useRef(null);
	const dispatch = useDispatch();
	const markers = useSelector((state) => state.marker);
	const isMobile = useMediaQuery({ maxWidth: 992 });
	const selectedMarker = markers.selectedMarker;
	const [minDate, setMinDate] = useState('');
	const [maxDate, setMaxDate] = useState('');
	const [state, setState] = useState({
		loadingMarkers: false,
		startDateRange: '',
		endDateRange: '',
		// items: [],
		timeline_options: {
			width: '100%',
			height: '160px',
			rtl: true,
			type: 'background',
			min: minDate,
			max: maxDate,
			stack: false,
			stackSubgroups: false,
			locale: 'fa',
			showCurrentTime: false,
			zoomMin: 1000 * 60 * 60 * 24, // one day in milliseconds
			margin: {
				item: 20,
				axis: 30
			},
			hiddenDates: {},
			tooltip: {
				followMouse: false,
				overflowMethod: 'flip',
				delay: 100
			},
			cluster: {
				fitOnDoubleClick: true,
				showStipes: false
			},
			loadingScreenTemplate: () => {
				return `<div class=${styles.loading}>در حال بارگزاری...</div>`;
			},
			template: (item, element, data) => {
				if (data.isCluster) {
					const grouped_items = data.items?.every(
						(item) => item.time === data.items[0].time
					);
					if (!grouped_items) {
						ReactDOM.render(
							<TimelineCluster title={data.items.length} />,
							element
						);
					} else {
						ReactDOM.render(
							<TimelineGroupItem title={data.items.length} />,
							element
						);
					}
				} else {
					ReactDOM.render(<TimelineSingleItem />, element);
				}
			}
		}
	});

	const getPointDate = (marker, type = 'start') => {
		const time = type === 'end' ? marker.end_date : marker.start_date;
		const timestamp = moment.unix(time).format('jYYYY-jMM-jDD');
		return timestamp;
	};

	const getSelectedMarker = useCallback(
		(prop) => {
			const selectedMarker = markers.list.find(
				(marker) => marker.id === prop.items[0]
			);
			dispatch(setSelectedMarker(selectedMarker));
			selectedMarker?.id === prop.items[0] &&
				isMobile &&
				dispatch(setSidebar(true));
		},
		[dispatch, markers.list, isMobile]
	);

	const destroyTimeline = useCallback(() => {
		if (instance.current) {
			instance.current.destroy();
		}
	}, [instance]);

	const randerTimelineRange = (start, end) => {
		setState((prev) => ({
			...prev,
			startDateRange: start,
			endDateRange: end
		}));
	};

	const onRangeChanged = useCallback(() => {
		const range = instance.current.getWindow();
		const start = moment(range?.start).format('YYYY/MM/DD');
		const end = moment(range?.end).format('YYYY/MM/DD');
		randerTimelineRange(start, end);
	}, []);

	const setTimeline = useCallback(
		(items) => {
			instance.current = new Timeline(
				timelineRef.current,
				items,
				state.timeline_options
			);

			instance.current.on('rangechanged', onRangeChanged);

			instance.current.on('select', (prop) => {
				getSelectedMarker(prop);
			});
		},
		[instance, state.timeline_options, getSelectedMarker, onRangeChanged]
	);

	// const setMarkerItems = useCallback(() => {
	// 	const items = [];
	// 	markers.list?.map((marker) => {
	// 		items.push({
	// 			id: marker.id,
	// 			content: marker.title,
	// 			start: getPointDate(marker),
	// 			end: getPointDate(marker, 'end'),
	// 			time: marker?.end_date || marker?.start_date,
	// 			group: '1',
	// 			align: 'center',
	// 			type: 'box',
	// 			title: moment.unix(marker?.start_date).format('h:m jYYYY/jM/jD')
	// 		});
	// 	});
	// 	console.log('items: ', items);
	// 	timelineItemsRef.current = items;
	// 	return items;
	// }, [markers.list]);
	// , [markers.list]);

	// const getMin = () => {
	// 	console.log('timelineItemRef.current: ', timelineItemRef.current);
	// 	return Math.min(...timelineItemRef.current.map((item) => item.time));
	// };

	// const getMinTime = useMemo(getMin, [getMin]);

	// const getMax = () => {
	// 	return Math.max(...timelineItemRef.current.map((item) => item.time));
	// };

	// const getMaxTime = useMemo(getMax, [getMax]);

	const initTimeline = useCallback(() => {
		const items = [];
		markers.list?.map((marker) => {
			items.push({
				id: marker.id,
				content: marker.title,
				start: getPointDate(marker),
				end: getPointDate(marker, 'end'),
				time: marker?.end_date || marker?.start_date,
				group: '1',
				align: 'center',
				type: 'box',
				title: moment.unix(marker?.start_date).format('h:m jYYYY/jM/jD')
			});
		});

		const min = Math.min(...items?.map((item) => item.time));
		const max = Math.max(...items?.map((item) => item.time));

		setMaxDate(moment.unix(max).add(50, 'Year').format('jYYYY-jMM-jDD'));
		setMinDate(
			moment.unix(min).subtract(50, 'Year').format('jYYYY-jMM-jDD')
		);

		setTimeline(items);

		if (!isObjEmpty(selectedMarker)) {
			instance.current.setSelection(selectedMarker.id, { focus: true });
		} else {
			instance.current.setSelection([]);
		}
	}, [setTimeline, selectedMarker, markers.list]);

	useEffect(() => {
		initTimeline();
		return () => {
			destroyTimeline();
		};
	}, [initTimeline, destroyTimeline]);

	return (
		<div className={styles.wrapper}>
			{/* {state.loadingMarkers && (
				<div className={styles.overlay}>در حال بروزرسانی</div>
			)} */}
			{state.startDateRange && (
				<div className={styles.range}>
					&#123;
					<div className={styles.rangeWrapper}>
						از
						<span className={styles.rangeItem}>
							{state.startDateRange}
						</span>
						تا
						<span className={styles.rangeItem}>
							{state.endDateRange}
						</span>
					</div>
					&#125;
				</div>
			)}
			<div
				className={cx(styles.timeline, 'vistimeline')}
				ref={timelineRef}></div>
		</div>
	);
});

VisTimelineVanillaJs.displayName = 'VisTimelineVanillaJs';
